import {
  BookOpen,
  Building2,
  Percent,
  ReceiptText,
  Warehouse as WarehouseIcon,
  Users,
  ToggleRight,
  ClipboardCheck,
  Network,
  Boxes,
  Smartphone,
  FileText,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Lead,
  P,
  H3,
  UL,
  LI,
  Code,
  Callout,
  Steps,
  Step,
  Flow,
  Lifecycle,
  Figure,
  DefList,
  Matrix,
  Checklist,
} from './components';
import type { GuideChapter } from './tracks';

function GoTo({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[12px] font-medium text-primary ring-1 ring-inset ring-primary/20 hover:bg-primary/15"
    >
      {children}
    </Link>
  );
}

// ── 1 · Overview ───────────────────────────────────────────────────────────
function Overview() {
  return (
    <div>
      <Lead>
        Before you run orders, stock or dispatch, get your <strong className="text-foreground">organization</strong>{' '}
        set up properly. A few minutes here makes every downstream document — invoices, challans,
        labels — correct and on-brand. Everything below lives in the{' '}
        <strong className="text-foreground">Workspace</strong> section of the left nav.
      </Lead>

      <H3>How your workspace is structured</H3>
      <Flow
        steps={[
          { icon: Network, title: 'Workspace', sub: 'your account', tone: 'muted' },
          { icon: Building2, title: 'Organization', sub: 'legal entity + GSTIN', tone: 'accent' },
          { icon: WarehouseIcon, title: 'Warehouses', sub: 'where stock lives' },
          { icon: Users, title: 'Members', sub: 'your team' },
          { icon: ReceiptText, title: 'Documents', sub: 'invoices, challans', tone: 'success' },
        ]}
      />
      <P>
        Your <strong>organization</strong> holds your legal identity (name, GSTIN, addresses, banking).
        That identity flows automatically onto every invoice and challan. Under it sit your{' '}
        <strong>warehouses</strong> (where stock is received and shipped from) and your{' '}
        <strong>members</strong> (the people who do the work, each with a role).
      </P>

      <H3>Your setup checklist</H3>
      <P>Work top to bottom — each links straight to the screen:</P>
      <Checklist
        items={[
          { label: 'Complete your organization profile (identity & address)', to: '/platform/organizations' },
          { label: 'Add your tax IDs and pick a pricing mode', to: '/platform/organizations' },
          { label: 'Set invoicing defaults & document numbering', to: '/platform/organizations' },
          { label: 'Add your banking details (shown on invoices)', to: '/platform/organizations' },
          { label: 'Create your warehouse(s)', to: '/platform/warehouses' },
          { label: 'Invite your team and assign roles', to: '/platform/users' },
        ]}
      />

      <Callout tone="info" title="Who can do this">
        Workspace setup is an <strong>admin</strong> task — the Workspace section (Organizations,
        Members, Warehouses) is visible only to workspace admins. Which <em>modules</em> you see
        (Orders, Dispatch, …) is set by your plan — see “Modules &amp; your plan” below.
      </Callout>
    </div>
  );
}

// ── 2 · Organization profile ─────────────────────────────────────────────────
function Profile() {
  return (
    <div>
      <Lead>
        Open <GoTo to="/platform/organizations">Organizations</GoTo> and select your organization. The
        profile is split into eight sections down a left rail — each has its own{' '}
        <strong>Edit</strong> button.
      </Lead>

      <Figure label="Workspace › Organizations › Profile" caption="Eight sections; edit any one in place.">
        <div className="grid gap-2 sm:grid-cols-2 text-[12.5px]">
          {[
            ['Profile', 'Display & legal name, code, business type, industry, website'],
            ['Tax & compliance', 'GSTIN, PAN, CIN, TAN, MSME, LUT + pricing mode'],
            ['Banking', 'Bank, branch, account, IFSC, UPI'],
            ['Invoicing defaults', 'FY start, net terms, return window'],
            ['Document numbering', 'Order / invoice prefixes, reset, padding'],
            ['Chatbot widget domains', 'Website + additional allowed origins'],
            ['Contact', 'Name, email, phone'],
            ['Registered address', 'Line 1/2, city, state, pincode, country'],
          ].map(([t, d]) => (
            <div key={t} className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
              <div className="font-semibold text-foreground">{t}</div>
              <div className="text-[11.5px] text-muted-foreground">{d}</div>
            </div>
          ))}
        </div>
      </Figure>

      <H3>Start with identity</H3>
      <DefList
        items={[
          { term: 'Display name', def: 'The friendly name used across the app and on the portal.' },
          { term: 'Legal name', def: <>Your registered entity name — this is what prints on tax invoices, so match it to your GST registration exactly.</> },
          { term: 'Business type', def: 'Sole proprietorship, Partnership, LLP, Private Limited, etc.' },
          { term: 'Website', def: <>Your public site. Also the primary origin that authorises your chatbot widget — see Chatbot widget domains.</> },
          { term: 'Registered address', def: 'Used on documents and to determine your home state for GST.' },
          { term: 'Logo', def: 'Upload once — it brands invoices, the portal and emails.' },
        ]}
      />

      <Callout tone="tip" title="Legal name = invoice name">
        Whatever you put in <strong>Legal name</strong> is the seller name on every GST invoice. Get
        it right once and it’s correct forever.
      </Callout>
    </div>
  );
}

// ── 3 · Tax & compliance ─────────────────────────────────────────────────────
function Tax() {
  return (
    <div>
      <Lead>
        Tax setup has two parts: your <strong className="text-foreground">tax identifiers</strong> (who
        you are to the GST system) and your <strong className="text-foreground">pricing mode</strong>{' '}
        (how the prices you type relate to tax). Both drive how invoices compute.
      </Lead>

      <H3>Tax identifiers</H3>
      <P>
        In the org profile’s <strong>Tax &amp; compliance</strong> section, add the IDs that apply to
        you:
      </P>
      <DefList
        items={[
          { term: 'GSTIN', def: <>Your GST number. Its first two digits are your <strong>state code</strong> — that’s how the system decides intra-state (CGST+SGST) vs inter-state (IGST) on every invoice.</> },
          { term: 'PAN / CIN / TAN', def: 'Entity tax IDs — printed where required on documents.' },
          { term: 'MSME / Udyam', def: 'Your MSME registration, if applicable.' },
          { term: 'LUT', def: <>Letter of Undertaking number — only if you export <em>without</em> paying IGST. (If you export <em>on payment of</em> IGST, leave this blank.)</> },
        ]}
      />

      <H3>Pricing mode</H3>
      <P>
        Set this once under <GoTo to="/settings/tax">Tax preferences</GoTo>. It’s{' '}
        <strong>workspace-wide</strong> and affects <em>new</em> orders only — each order is stamped
        with the mode active when it was created, and its invoice locks the same mode.
      </P>
      <DefList
        items={[
          { term: 'Tax-exclusive', def: <>The price you enter is the <strong>net</strong> amount; GST is added on top at invoicing (₹1,000 + 18% = ₹1,180). Typical for B2B, services, wholesale.</> },
          { term: 'Tax-inclusive (MRP)', def: <>The price you enter <strong>already includes</strong> tax; the invoice extracts the embedded GST (₹1,180 MRP = ₹1,000 + ₹180). Typical for apparel, retail, B2C portals.</> },
        ]}
      />

      <Callout tone="warn" title="Pick the mode before you sell">
        Switching later doesn’t rewrite past orders — only new ones use the new mode. Choose the one
        that matches how you quote prices and leave it.
      </Callout>
    </div>
  );
}

// ── 4 · Invoicing & numbering ─────────────────────────────────────────────────
function Invoicing() {
  return (
    <div>
      <Lead>
        Three small sections control how your sales paperwork looks and is numbered: invoicing
        defaults, document numbering, and banking.
      </Lead>

      <H3>Invoicing defaults</H3>
      <DefList
        items={[
          { term: 'FY start month', def: 'Your financial year start (April in India) — anchors yearly numbering resets and reports.' },
          { term: 'Net terms (days)', def: 'Default payment terms — the due date offset printed on invoices.' },
          { term: 'Return window (days)', def: <>How long customers have to request a return. Drives the post-sales return flow.</> },
        ]}
      />

      <H3>Document numbering</H3>
      <P>
        Your order and invoice numbers are built from a prefix + period + a running counter, so they’re
        sequential and audit-friendly.
      </P>
      <Figure label="Workspace › Organizations › Document numbering" caption="Prefix + period + padded counter.">
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="rounded-md bg-muted px-2 py-1 font-mono text-foreground">SO</span>
          <span className="text-muted-foreground">+</span>
          <span className="rounded-md bg-muted px-2 py-1 font-mono text-foreground">202601</span>
          <span className="text-muted-foreground">+</span>
          <span className="rounded-md bg-muted px-2 py-1 font-mono text-foreground">001</span>
          <span className="text-muted-foreground">→</span>
          <span className="rounded-md bg-primary/10 px-2 py-1 font-mono font-semibold text-primary ring-1 ring-inset ring-primary/20">SO202601001</span>
        </div>
      </Figure>
      <UL>
        <LI><strong>Order / Invoice prefix</strong> — e.g. <Code>SO</Code> and <Code>INV</Code>.</LI>
        <LI><strong>Reset period</strong> — when the counter restarts: <Code>MONTHLY</Code>, <Code>YEARLY</Code>, <Code>FISCAL_YEAR</Code> or <Code>NEVER</Code>.</LI>
        <LI><strong>Counter digits</strong> — zero-padding (e.g. <Code>001</Code>), plus a separator and an effective-from date.</LI>
      </UL>

      <H3>Banking</H3>
      <P>
        Fill in <strong>Banking</strong> (bank, branch, account number, IFSC, UPI). It appears in the
        <strong> “Pay to” </strong> block on every invoice, so customers know exactly where to remit.
      </P>
      <Callout tone="info" title="Set numbering before your first invoice">
        Changing prefixes mid-year is fine, but the cleanest audit trail comes from setting your
        scheme before you raise document #1.
      </Callout>
    </div>
  );
}

// ── 5 · Warehouses ───────────────────────────────────────────────────────────
function Warehouses() {
  return (
    <div>
      <Lead>
        A <strong className="text-foreground">warehouse</strong> is any place you hold or move stock.
        Every goods receipt, dispatch and stock balance is tied to one — so create at least one before
        you receive materials or ship orders.
      </Lead>

      <P>
        Go to <GoTo to="/platform/warehouses">Warehouses</GoTo> → <strong>Add warehouse</strong>.
      </P>

      <Steps>
        <Step title="Name & code it">
          Give it a <strong>code</strong> (e.g. <Code>WH-MUM-A</Code>) and a name. The code shows up in
          pickers and on documents.
        </Step>
        <Step title="Choose a type">
          <strong>Warehouse</strong> (stocks inventory), <strong>Retail store</strong> (sells direct +
          holds shelf stock), <strong>Cross-dock hub</strong> (transit only), <strong>Virtual</strong>{' '}
          (bookkeeping only), or <strong>3PL</strong> (operated by a partner).
        </Step>
        <Step title="Add address, contact & manager">
          The address prints on documents and feeds carrier pickups. Optionally set a manager (picked
          from your active members), capacity, operating hours and a photo.
        </Step>
        <Step title="GSTIN (optional)">
          Set a warehouse-level GSTIN only if this location bills under a different number; otherwise it
          falls back to your organization’s GSTIN automatically.
        </Step>
      </Steps>

      <Callout tone="tip" title="Plan limits">
        Your plan caps how many warehouses you can add — the header shows “{`{used} of {max} used`}”. If
        you hit the cap, the button disables with an upgrade prompt.
      </Callout>
    </div>
  );
}

// ── 6 · Members & roles ──────────────────────────────────────────────────────
function Members() {
  return (
    <div>
      <Lead>
        Add your team under <GoTo to="/platform/users">Members</GoTo> and give each person a role.
        Roles keep responsibilities clear — requesters, approvers and operators stay distinct.
      </Lead>

      <P>
        Click <strong>Invite member</strong>, enter their name, email, phone and role, and optionally
        scope them to one organization (leave blank for the whole workspace). They appear as{' '}
        <Code>invited</Code> until they accept.
      </P>

      <Lifecycle
        states={[
          { label: 'invited', tone: 'warn' },
          { label: 'active', tone: 'success' },
        ]}
        branches={[{ from: 'active', to: [{ label: 'inactive', tone: 'muted' }] }]}
      />

      <H3>The four roles</H3>
      <Matrix
        columns={['Admin', 'Manager', 'Operator', 'Viewer']}
        rows={[
          { label: 'View dashboards & records', cells: [true, true, true, true] },
          { label: 'Day-to-day execution (orders, dispatch, GRN…)', cells: [true, true, true, false] },
          { label: 'Approvals & reporting', cells: [true, true, false, false] },
          { label: 'Manage workspace (org, members, warehouses)', cells: [true, false, false, false] },
        ]}
      />
      <DefList
        items={[
          { term: 'Admin', def: 'Full tenant access — owns workspace setup and billing.' },
          { term: 'Manager', def: 'Operations + reporting — runs the floor and approves.' },
          { term: 'Operator', def: 'Day-to-day execution — picks, packs, receives, dispatches.' },
          { term: 'Viewer', def: 'Read-only — dashboards and records, no changes.' },
        ]}
      />

      <Callout tone="tip" title="Separation of duties">
        For spend control, keep the person who <em>raises</em> a purchase request different from the
        one who <em>approves</em> it. Roles make that easy. Seats are capped by your plan — the header
        shows seats used.
      </Callout>
    </div>
  );
}

// ── 7 · Modules & plan ───────────────────────────────────────────────────────
function Modules() {
  return (
    <div>
      <Lead>
        The modules in your left nav — Inventory, Orders, Dispatch, Returns, Reports and so on — are
        switched on by your <strong className="text-foreground">plan</strong>. You’ll only see what
        you’re subscribed to, which keeps the workspace focused on what you actually use.
      </Lead>

      <H3>How it works</H3>
      <UL>
        <LI>Each module is a <strong>feature</strong> enabled on your subscription.</LI>
        <LI>If a feature is on, its nav item appears; if not, it’s hidden — no clutter.</LI>
        <LI>Module access is managed by <strong>Zafroi</strong> as part of onboarding and your plan, so it’s consistent and billable — it isn’t a self-serve toggle in the workspace.</LI>
      </UL>

      <Callout tone="info" title="Want a module turned on?">
        To add Dispatch, Returns, Reports or any other module, talk to Zafroi (or your onboarding
        contact) to adjust your plan — it activates for your whole workspace once enabled.
      </Callout>

      <H3>The operator mobile app</H3>
      <P>
        Your floor team doesn’t need the full desktop ERP. Under{' '}
        <GoTo to="/platform/mobile-app">Mobile app</GoTo> you’ll find a QR code and install steps for
        the <strong>operator PWA</strong> (Android + iOS) — a stripped-down app for scanning, packing
        and returns.
      </P>
      <Flow
        steps={[
          { icon: Smartphone, title: 'Scan QR', sub: 'from Mobile app', tone: 'accent' },
          { icon: BookOpen, title: 'Install PWA', sub: 'add to home screen' },
          { icon: Boxes, title: 'Operators work', sub: 'pack · scan · returns', tone: 'success' },
        ]}
      />
    </div>
  );
}

// ── 8 · Go-live checklist ─────────────────────────────────────────────────────
function GoLive() {
  return (
    <div>
      <Lead>
        A final pass before you start transacting. Tick these off and your documents, stock and team
        are all production-ready.
      </Lead>

      <Checklist
        items={[
          { label: 'Legal name matches your GST registration', to: '/platform/organizations' },
          { label: 'GSTIN added (drives CGST/SGST vs IGST)', to: '/platform/organizations' },
          { label: 'Pricing mode chosen (exclusive vs MRP)', to: '/settings/tax' },
          { label: 'Invoice/order numbering set', to: '/platform/organizations' },
          { label: 'Banking details filled (invoice “Pay to”)', to: '/platform/organizations' },
          { label: 'Logo uploaded', to: '/platform/organizations' },
          { label: 'At least one warehouse created', to: '/platform/warehouses' },
          { label: 'Team invited with the right roles', to: '/platform/users' },
        ]}
      />

      <H3>Common pitfalls</H3>
      <UL>
        <LI><strong>Display name on invoices instead of legal name</strong> — set the Legal name field; that’s what tax invoices use.</LI>
        <LI><strong>No GSTIN</strong> — without it the system can’t split CGST/SGST vs IGST. Add it first.</LI>
        <LI><strong>Wrong pricing mode after you’ve sold</strong> — it only affects new orders, so decide up front.</LI>
        <LI><strong>No warehouse</strong> — you can’t receive stock or dispatch without one.</LI>
      </UL>

      <Callout tone="success" title="You’re set up">
        Identity, tax, numbering, banking, warehouses and team — all done. Next, learn{' '}
        <Link to="/guide/procurement" className="font-medium text-primary hover:underline">Procurement</Link>{' '}
        to bring materials in, then sell finished goods.
      </Callout>
    </div>
  );
}

// ── Chapters export ──────────────────────────────────────────────────────────
export const organizationChapters: GuideChapter[] = [
  { id: 'overview', title: 'Overview', icon: Network, render: Overview },
  { id: 'profile', title: 'Organization profile', icon: Building2, render: Profile },
  { id: 'tax', title: 'Tax & compliance', icon: Percent, render: Tax },
  { id: 'invoicing', title: 'Invoicing & numbering', icon: FileText, render: Invoicing },
  { id: 'warehouses', title: 'Warehouses', icon: WarehouseIcon, render: Warehouses },
  { id: 'members', title: 'Members & roles', icon: Users, render: Members },
  { id: 'modules', title: 'Modules & your plan', icon: ToggleRight, render: Modules },
  { id: 'go-live', title: 'Go-live checklist', icon: ClipboardCheck, render: GoLive },
];
